import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { History, TrendingUp, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { format } from 'date-fns';

interface BuyingPowerData {
  id: string;
  created_at: string;
  inputs: {
    homePrice?: number;
    desiredPayment?: number;
  };
  calculated: {
    monthlyPayment?: number;
    maxHomePrice?: number;
    buyingPower?: number;
  };
}

const RecentCalculationsCard: React.FC = () => {
  const { user } = useAuth();

  const { data: recentCalcs, isLoading } = useQuery({
    queryKey: ['recentCalculations', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('buying_power_data')
        .select('id, created_at, inputs, calculated')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(3);
      
      if (error) throw error;
      return data as BuyingPowerData[];
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  if (isLoading || !recentCalcs || recentCalcs.length === 0) {
    return null;
  }

  const formatCurrency = (value: number | undefined) => {
    if (!value) return '--';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
      className="w-full max-w-4xl"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-sm font-medium text-muted-foreground">Recent Calculations</h3>
        </div>
        <Button asChild variant="ghost" size="sm" className="text-xs text-muted-foreground hover:text-foreground">
          <Link to="/calculator">
            View All
            <ArrowRight className="ml-1 w-3 h-3" />
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {recentCalcs.map((calc, index) => {
          const homePrice = calc.inputs?.homePrice || calc.calculated?.maxHomePrice;
          const monthlyPayment = calc.calculated?.monthlyPayment;
          const buyingPower = calc.calculated?.buyingPower || calc.calculated?.maxHomePrice;

          return (
            <motion.div
              key={calc.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Card className="bg-slate-800/30 border-border/40 hover:bg-slate-800/50 transition-colors cursor-pointer group">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(calc.created_at), 'MMM d')}
                    </span>
                    <TrendingUp className="w-3 h-3 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <p className="text-lg font-semibold text-foreground">
                    {formatCurrency(buyingPower || homePrice)}
                  </p>
                  {monthlyPayment && (
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(monthlyPayment)}/mo
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default RecentCalculationsCard;
